import React, { useEffect, useState } from 'react'
import { ApolloProvider } from '@apollo/client'
import { camelCase } from 'lodash'
import { useLingui } from '@lingui/react'
import { Flex } from 'front-end/src/components'
import LooopPrivateRoutes from './LooopPrivateRoutes'
import { usePublicApiSession } from '../public-api/hooks/usePublicApiSession'
import { loadLocaleMessages } from '../i18n/i18n'
import { SupportedLocalesEnum } from '../graphql/private/PrivateSchemaTypes'
import { LooopPublicRoutes } from './LooopPublicRoutes'
import {
  apolloClientPrivate,
  apolloClientPublic
} from '../graphql/apolloClient'
import { MainHeader, MainHeaderPublic } from './mainheader/MainHeader'
import { LanguageDropdown } from './common/language-dropdown/LanguageDropdown'
import {
  UserPatchAsSelfDocument,
  UserPatchAsSelfMutation,
  UserPatchAsSelfMutationVariables
} from '../pages/user/UserProfilePage.codegen'
import { commonReactSelectStyles, SelectVariant } from './common/select/Select'
import { brandColors } from 'shared/basicPalette'
import { ThirdPartyIntegrations } from './utils/ThirdPartyIntegrations'
import { useBackButtonHistory } from './common/back-button/useBackButtonHistory'
import { ignoredBackButtonRoutes } from 'shared/linkBuilder'
import { Redirect, useLocation } from 'react-router-dom'
import { FullBlockSpinner } from './common/full-block-spinner/FullBlockSpinner'
import { RenderedComponentContext } from './RenderedComponentContext'

const languageDropdownStyles = {
  control: (styles, { isFocused }) => ({
    ...styles,
    ...commonReactSelectStyles(SelectVariant.GREY),
    backgroundColor: 'transparent',
    borderColor: isFocused ? brandColors.green[600] : brandColors.grey.light,
    ':hover': {
      borderColor: isFocused ? brandColors.green[600] : brandColors.grey.mid
    }
  }),
  container: (styles) => ({
    ...styles,
    margin: '0 auto'
  })
}

async function initUserLocale(locale?: SupportedLocalesEnum) {
  try {
    await loadLocaleMessages({ locale })
  } catch (error) {
    console.error(error)
  }
}

async function updateUserLocale(locale: SupportedLocalesEnum) {
  if (!locale) {
    return
  }
  try {
    await apolloClientPrivate.mutate<
      UserPatchAsSelfMutation,
      UserPatchAsSelfMutationVariables
    >({
      mutation: UserPatchAsSelfDocument,
      variables: {
        input: { locale }
      }
    })
  } catch (error) {
    console.error(error)
  }
}

const temporaryLocaleLocalStorageKey = 'temporaryLocale'

/**
 * probably the most important react component in the whole app. It decides which if the two main routers to render
 */
export const LooopRouters: React.FC = () => {
  const [renderedComponentName, setRenderedComponentName] = useState('')
  const { i18n } = useLingui()
  const location = useLocation()
  const [messagesLoading, setMessagesLoading] = useState<boolean>(true)
  const [locale, setLocale] = useState<SupportedLocalesEnum>()
  const temporaryLocale = localStorage?.getItem(temporaryLocaleLocalStorageKey)
  const currentLocale = camelCase(i18n.locale) as SupportedLocalesEnum
  const { session, loading: sessionLoading } = usePublicApiSession()
  const loading = sessionLoading || messagesLoading

  useBackButtonHistory({ rules: ignoredBackButtonRoutes })

  useEffect(() => {
    if (temporaryLocale) {
      setLocale(temporaryLocale as SupportedLocalesEnum)
    }
    localStorage?.removeItem(temporaryLocaleLocalStorageKey)
  }, [session])

  // handle only unauth actions
  useEffect(() => {
    if (session || sessionLoading) {
      return
    }

    initUserLocale().finally(() => setMessagesLoading(false))
  }, [session, sessionLoading])

  // handle only auth actions
  useEffect(() => {
    if (!session) {
      return
    }

    let userLocale = session?.user?.locale as SupportedLocalesEnum

    // if user is new, his userLocale will be null, so we have to save it in user profile
    if (!userLocale) {
      updateUserLocale(currentLocale)
      initUserLocale(currentLocale).finally(() => setMessagesLoading(false))
      return
    }

    // switch to user locale after auth if his browser locale || org locale is different from the auth state
    if (!locale && currentLocale !== userLocale) {
      initUserLocale(userLocale).finally(() => setMessagesLoading(false))
      return
    }

    // patch auth user locale with new value if he manually changed it on login screen
    if (locale && locale !== userLocale) {
      updateUserLocale(locale)
      initUserLocale(locale).finally(() => setMessagesLoading(false))
    }
  }, [session, locale])

  useEffect(() => {
    // Due to issues with onNavigationStateChange we use post message to detect when mobile app should register for PushNotifications
    if (session && window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage('RegisterMobileDevice')
    }
  }, [session])

  const handleLocaleChange = async (data) => {
    try {
      await loadLocaleMessages({ locale: data })
      setLocale(data)
      localStorage?.setItem(temporaryLocaleLocalStorageKey, data)
    } catch (error) {
      console.error(error)
    }
  }

  if (loading) {
    // this spinner is important-without it we might render bad routes before we actually know if the user is logged in or not causing extra react rerenders
    return <FullBlockSpinner />
  }

  // needed to redirect from old links with hash inside
  if (/^#\//.test(location.hash)) {
    return <Redirect to={location.hash.replace('#', '')} />
  }

  if (session === null) {
    return (
      <ApolloProvider client={apolloClientPublic}>
        <MainHeaderPublic />
        <RenderedComponentContext.Provider
          value={{
            renderedComponentName,
            setRenderedComponentName
          }}
        >
          <LooopPublicRoutes />
        </RenderedComponentContext.Provider>
        <Flex as="footer" py="24px" justify="center">
          <LanguageDropdown
            value={currentLocale}
            styles={languageDropdownStyles}
            onChange={handleLocaleChange}
          />
        </Flex>
      </ApolloProvider>
    )
  }

  return (
    <ApolloProvider client={apolloClientPrivate}>
      <MainHeader />
      <RenderedComponentContext.Provider
        value={{
          renderedComponentName,
          setRenderedComponentName
        }}
      >
        <LooopPrivateRoutes />
      </RenderedComponentContext.Provider>
      <ThirdPartyIntegrations />
    </ApolloProvider>
  )
}
